import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import {
	AlertService,
	RestService,
	SessionService,
	TypeaheadService,
} from "@recore-solutions/nstar-lib";
import { Cacheable } from "ts-cacheable";
import { merge, Observable, Subscription } from "rxjs";
import { filter, map, startWith, tap } from "rxjs/operators";
import { Alert } from "src/app/alert";
import { PreferencesService } from "src/app/preferences/preferences.service";
import { BulkListingSearchFilter } from "src/app/shared/interfaces/bulk-listing-search-filter";
import { BulkListingsQuery } from "src/app/shared/interfaces/bulk-listings-query";
import { environment } from "src/environments/environment";
import * as CacheBusters from "../../shared/cache-busters";
import { HomeListingResultsService } from "../home-listing-results.service";
import { ConfigurationService } from "src/app/configuration.service";

@Component({
	selector: "app-home-header-search",
	templateUrl: "./home-header-search.component.html",
	styleUrls: ["./home-header-search.component.scss"],
	providers: [{ provide: "SearchFilterTypeahead", useClass: TypeaheadService }],
})
export class HomeHeaderSearchComponent implements OnInit, OnDestroy {
	@Input() control: UntypedFormControl;
	@Input() placeholder: string;
	@Output() updateSearch: EventEmitter<BulkListingsQuery> = new EventEmitter();
	private searchControl: UntypedFormControl;
	searchFilter$: Observable<BulkListingSearchFilter[]>;
	private recentSearchControl: UntypedFormControl;
	recentSearchFilter$: Observable<BulkListingSearchFilter[]>;
	private savedSearchControl: UntypedFormControl;
	savedSearch$: Observable<{ name: string; filters: BulkListingSearchFilter[] }[]>;

	private cachedSearchSuggestions = {};

	private subscriptions: Subscription[] = [];

	constructor(
		private restService: RestService,
		private homeListingResultsService: HomeListingResultsService,
		private preferencesService: PreferencesService,
		@Inject("SearchFilterTypeahead") public searchFilterTypeahead: TypeaheadService<any>,
		private alertService: AlertService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private sessionService: SessionService,
		private configService: ConfigurationService
	) {}

	ngOnInit(): void {
		this.searchControl =
			this.control ||
			(this.homeListingResultsService.listingSummaryRequestForm.get(
				"search"
			) as UntypedFormControl);
		this.searchFilter$ = this.searchControl.valueChanges.pipe(startWith(this.searchControl.value));
		this.subscriptions.push(
			this.searchControl.valueChanges
				.pipe(
					tap((searchControlValue) => this.updateSearch.emit(searchControlValue)),
					filter(
						(value) =>
							!!value &&
							value.length > 0 &&
							!this.control &&
							!this.activatedRoute?.snapshot?.data?.stayHereOnSearch
					),
					tap((_) => this.router.navigate(["/"]))
				)
				.subscribe()
		);
		this.recentSearchControl = new UntypedFormControl([]);
		this.recentSearchFilter$ = merge(
			this.preferencesService
				.getPreference(["recentSearchFilters"])
				.pipe(tap((value) => this.recentSearchControl.setValue(value || []))),
			this.recentSearchControl.valueChanges
		);
		this.savedSearchControl = new UntypedFormControl([]);
		this.savedSearch$ = merge(
			this.preferencesService
				.getPreference(["savedSearches"])
				.pipe(tap((value) => this.savedSearchControl.setValue(value || []))),
			this.savedSearchControl.valueChanges
		);
		this.placeholder =
			this.placeholder || "Filter by MLS#, listing agent, office, address, or city.";
		this.searchFilterTypeahead.configuration.filter = (searchText: string) =>
			this.getSearchSuggestions(searchText);
		this.searchFilterTypeahead.configuration.resultsTransform = map(this.mapResultsToResultGroups);
		const listingStatusCDControl =
			this.homeListingResultsService.listingSummaryRequestForm.get("listingStatusCD");
		listingStatusCDControl.valueChanges
			.pipe(
				startWith(listingStatusCDControl.value),
				filter((_) => !this.control),
				map((value) => value.join(",").toLowerCase().split(",")),
				map((value) => value.indexOf("i") > -1),
				tap((value) =>
					!!value
						? this.alertService.alertInfo(
								"Incomplete listings are removed after 21 days from the time they were first created, regardless of any later edits.",
								{
									collapsible: true,
									startCollapsed: true,
									collapseDelay: 5,
									permanent: true,
									bodyIcon: "calendar",
								},
								"incomplete"
						  )
						: this.alertService.removeUniqueAlert("incomplete")
				)
			)
			.subscribe();
	}

	@Cacheable({
		cacheBusterObserver: CacheBusters.listingOptionsCacheBuster$,
		maxAge: 60 * 60 * 1000,
		maxCacheCount: 99,
	})
	getSearchSuggestions(searchText: string): Observable<BulkListingSearchFilter[]> {
		return this.restService
			.get<BulkListingSearchFilter[]>(
				environment.endpoints.searchSuggestions +
					`?queryString=${searchText}&sinceDate=1900-01-01 12:00:00&pageNumber=1&pageSize=1000`
			)
			.pipe(
				map((suggestions) =>
					!!this.control ? suggestions.filter((s) => s.type !== "mlid") : suggestions
				)
			);
	}

	removeIncompleteAlertFn(incompleteAlert: Alert): (needsIncompleteAlert: boolean) => void {
		return (needsIncompleteAlert: boolean) => {
			if (!needsIncompleteAlert) {
				this.alertService.removeAlert(incompleteAlert);
			}
		};
	}

	mapResultsToResultGroups(results: any[]): any[] {
		const activeGroups = results.reduce((a, c) => ({ ...a, [c.type]: [c.typeName] }), {});
		const inactiveGroups = results.reduce((a, c) => ({ ...a, [c.type]: [c.typeName] }), {});
		const groups = {
			true: activeGroups,
			false: inactiveGroups,
		};
		return results.map((r) => ({
			...r,
			resultGroup: groups[r.isActive]?.[r.type] ? groups[r.isActive][r.type].pop() : null,
		}));
	}

	addSearchFilter(event: NgbTypeaheadSelectItemEvent): void {
		if (!!event.preventDefault) {
			event.preventDefault();
		}

		if (
			event.item.type === "Listing_ID" &&
			(event.item.isActive ||
				this.sessionService.permissionLevels.some((pl) =>
					this.configService.config.permissions.navigateToDeleted.includes(
						pl?.accessName?.toLocaleLowerCase()
					)
				))
		) {
			this.updateSearch.emit(this.searchControl.value);
			this.router.navigate(["/", "listing", event.item.value]);
		} else {
			const searchFilters = this.stripSearchFilter(
				this.searchControl.value || [],
				event.item
			).concat(event.item);
			this.searchControl.setValue(searchFilters);
			const recentFilters = this.stripSearchFilter(
				this.recentSearchControl.value || [],
				event.item
			).concat(event.item);
			const newRecentFilters = recentFilters.slice(Math.max(recentFilters.length - 50, 0));
			this.recentSearchControl.setValue(newRecentFilters);
			if (!this.control) {
				this.preferencesService
					.setPreference(newRecentFilters, ["recentSearchFilters"])
					.subscribe();
			}
		}
	}

	clearSearchFilters(): void {
		this.searchControl.setValue([]);
	}

	addSearchFilters(filters: BulkListingSearchFilter[]): void {
		filters.forEach((item) => setTimeout(() => this.addSearchFilter({ item } as any)));
	}

	setSearchFilters(filters: BulkListingSearchFilter[]): void {
		this.searchControl.setValue(filters || []);
	}

	removeSearchFilter(filterToRemove: BulkListingSearchFilter): void {
		const searchFilters = this.stripSearchFilter(this.searchControl.value || [], filterToRemove);
		this.searchControl.setValue(searchFilters);
	}

	stripSearchFilter(
		filters: BulkListingSearchFilter[],
		filterToRemove: BulkListingSearchFilter
	): BulkListingSearchFilter[] {
		return filters.filter(
			(f) => !(f.type === filterToRemove.type && f.value === filterToRemove.value)
		);
	}

	stripSearchFilters(
		filters: BulkListingSearchFilter[],
		strip: BulkListingSearchFilter[]
	): BulkListingSearchFilter[] {
		return filters.filter((f) => !strip.find((s) => s.type === f.type && s.value === f.value));
	}

	saveSearch(filters: BulkListingSearchFilter[], name: string = new Date().toString()): void {
		const savedSearches = this.savedSearchControl.value ? [...this.savedSearchControl.value] : [];
		savedSearches.push({ name, filters });
		this.savedSearchControl.setValue(savedSearches);
		this.preferencesService.setPreference(savedSearches, ["savedSearches"], true).subscribe((_) =>
			this.alertService.addAlert({
				body: `Search filters saved as "${name}"!`,
				cssClass: "bg-success text-light",
				delay: 5000,
			})
		);
	}

	removeSavedSearch(savedSearch: { name: string }): void {
		const savedSearches = (
			this.savedSearchControl.value ? [...this.savedSearchControl.value] : []
		).filter((s) => s.name !== savedSearch.name);
		this.savedSearchControl.setValue(savedSearches);
		this.preferencesService.setPreference(savedSearches, ["savedSearches"], true).subscribe((_) =>
			this.alertService.addAlert({
				body: `Saved Search "${savedSearch.name}" removed!`,
				cssClass: "bg-success text-light",
				delay: 5000,
			})
		);
	}

	typeaheadKeydown(typeaheadInstance: NgbTypeahead, _$event: KeyboardEvent) {
		if (typeaheadInstance.isPopupOpen()) {
			setTimeout(() => {
				const popup = document.getElementById(typeaheadInstance.popupId);
				if (!!popup) {
					const activeElements = popup.getElementsByClassName("active");
					if (activeElements.length === 1) {
						const elem = activeElements[0] as any;
						if (
							elem.offsetTop + elem.offsetHeight >
							elem.parentNode.offsetHeight + elem.parentNode.scrollTop
						) {
							elem.parentNode.scrollTop =
								elem.offsetTop + elem.offsetHeight - elem.parentNode.offsetHeight;
						} else if (elem.offsetTop < elem.parentNode.scrollTop) {
							elem.parentNode.scrollTop = elem.offsetTop;
						}
					}
				}
			});
		}
	}

	ngOnDestroy(): void {
		this.alertService.removeUniqueAlert("incomplete");
		this.subscriptions.forEach((s) => s.unsubscribe());
	}
}
